import type {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  UserQueryParams,
  PaginatedResponse,
  ApiResponse,
  Company,
  Ticket,
} from '@/types/api';
import type { AxiosProgressEvent } from 'axios';
import { getUserRoleFromString, UserRole } from '@/types/api';
import { apiClient } from './api';
import { apiEndpoints, appConfig, storageKeys } from '@/config/app';

type AgentDirectoryUser = Omit<User, 'role'> & {
  role: UserRole | string;
  avatarUrl?: string;
};

interface AgentDirectoryPage {
  data?: AgentDirectoryUser[];
  items?: AgentDirectoryUser[];
  totalCount?: number;
  page?: number;
  pageNumber?: number;
  pageSize?: number;
  totalPages?: number;
  hasNext?: boolean;
  hasNextPage?: boolean;
  hasPrevious?: boolean;
  hasPreviousPage?: boolean;
}

export class UserService {
  /**
   * Get paginated list of users with filtering and sorting
   */
  async getUsers(params: UserQueryParams = {}): Promise<ApiResponse<PaginatedResponse<User>>> {
    const queryParams = new URLSearchParams();

    // Add pagination
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    // Add filters
    if (params.role) {
      params.role.forEach(role => queryParams.append('role', role.toString()));
    }
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.companyId) queryParams.append('companyId', params.companyId);
    if (params.search) queryParams.append('search', params.search);

    // Add sorting
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = `${apiEndpoints.users.list}?${queryParams.toString()}`;
    return await apiClient.get<PaginatedResponse<User>>(url);
  }

  /**
   * Get a specific user by ID
   */
  async getUser(userId: string): Promise<ApiResponse<User>> {
    return await apiClient.get<User>(apiEndpoints.users.get(userId));
  }

  /**
   * Create a new user
   */
  async createUser(userData: CreateUserRequest): Promise<ApiResponse<User>> {
    return await apiClient.post<User>(apiEndpoints.users.create, userData);
  }

  /**
   * Update an existing user
   */
  async updateUser(userId: string, updates: UpdateUserRequest): Promise<ApiResponse<User>> {
    return await apiClient.put<User>(apiEndpoints.users.update(userId), updates);
  }

  /**
   * Delete a user (deactivate)
   */
  async deleteUser(userId: string): Promise<ApiResponse<void>> {
    return await apiClient.delete<void>(apiEndpoints.users.delete(userId));
  }

  /**
   * Activate a user
   */
  async activateUser(userId: string): Promise<ApiResponse<void>> {
    return await apiClient.post<void>(apiEndpoints.users.activate(userId));
  }

  /**
   * Deactivate a user
   */
  async deactivateUser(userId: string): Promise<ApiResponse<void>> {
    return await apiClient.post<void>(apiEndpoints.users.deactivate(userId));
  }

  /**
   * Get current user profile
   */
  async getCurrentUserProfile(): Promise<ApiResponse<User>> {
    return await apiClient.get<User>(apiEndpoints.users.profile);
  }

  /**
   * Update current user profile
   */
  async updateProfile(updates: Partial<User>): Promise<ApiResponse<User>> {
    return await apiClient.put<User>(apiEndpoints.users.profile, updates);
  }

  /**
   * Get users by role
   */
  async getUsersByRole(role: UserRole, params: Partial<UserQueryParams> = {}): Promise<ApiResponse<PaginatedResponse<User>>> {
    return await this.getUsers({
      ...params,
      role: [role],
    });
  }

  /**
   * Get active agents for ticket assignment (allowed for agents and higher)
   */
  async getAgents(params: Partial<UserQueryParams> = {}): Promise<ApiResponse<PaginatedResponse<User>>> {
    const query = new URLSearchParams();
    if (params.page) query.append('page', params.page.toString());
    query.append('pageSize', (params.pageSize || 100).toString());
    const res = await apiClient.get<AgentDirectoryPage>(`${apiEndpoints.users.agents}?${query.toString()}`);
    if (res.success && res.data) {
      const rawAgentUsers = res.data.data || res.data.items || [];
      const agentUsers: User[] = rawAgentUsers.map(agent => ({
        ...agent,
        fullName: agent.fullName || `${agent.firstName} ${agent.lastName}`.trim(),
        profileImageUrl: agent.profileImageUrl || agent.avatarUrl,
        role: typeof agent.role === 'string' ? getUserRoleFromString(agent.role) : agent.role,
      }));
      return {
        ...res,
        data: {
          data: agentUsers,
          totalCount: res.data.totalCount ?? agentUsers.length,
          pageNumber: res.data.pageNumber ?? res.data.page ?? 1,
          pageSize: res.data.pageSize ?? params.pageSize ?? 100,
          totalPages: res.data.totalPages ?? (agentUsers.length > 0 ? 1 : 0),
          hasNextPage: res.data.hasNextPage ?? res.data.hasNext ?? false,
          hasPreviousPage: res.data.hasPreviousPage ?? res.data.hasPrevious ?? false,
        },
      };
    }
    return {
      success: res.success,
      data: {
        data: [],
        totalCount: 0,
        pageNumber: params.page ?? 1,
        pageSize: params.pageSize ?? 100,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      message: res.message,
      errors: res.errors,
    };
  }

  /**
   * Load the complete active staff directory for assignment controls.
   * The API intentionally caps each page at 100 records, so dropdowns must
   * traverse every page instead of silently omitting valid assignees.
   */
  async getAllAgents(pageSize = 100): Promise<ApiResponse<PaginatedResponse<User>>> {
    const agents: User[] = [];
    let page = 1;

    while (true) {
      const response = await this.getAgents({ page, pageSize });

      if (!response.success || !response.data) {
        return response;
      }

      agents.push(...response.data.data);
      if (!response.data.hasNextPage) {
        return {
          ...response,
          data: {
            data: agents,
            totalCount: response.data.totalCount,
            pageNumber: 1,
            pageSize: agents.length,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        };
      }

      page += 1;
    }
  }

  /**
   * Get all end users
   */
  async getEndUsers(params: Partial<UserQueryParams> = {}): Promise<ApiResponse<PaginatedResponse<User>>> {
    return await this.getUsers({
      ...params,
      role: [UserRole.EndUser],
    });
  }

  /**
   * Get all admins
   */
  async getAdmins(params: Partial<UserQueryParams> = {}): Promise<ApiResponse<PaginatedResponse<User>>> {
    return await this.getUsers({
      ...params,
      role: [UserRole.Admin],
    });
  }

  /**
   * Search users
   */
  async searchUsers(query: string, filters?: Partial<UserQueryParams>): Promise<ApiResponse<PaginatedResponse<User>>> {
    return await this.getUsers({
      search: query,
      ...filters,
    });
  }

  /**
   * Reset user password
   */
  async resetUserPassword(userId: string): Promise<ApiResponse<{ message: string; tempPassword: string }>> {
    return await apiClient.post<{ message: string; tempPassword: string }>(apiEndpoints.users.resetPassword(userId));
  }

  /**
   * Bulk update users
   */
  async bulkUpdateUsers(userIds: string[], updates: UpdateUserRequest): Promise<ApiResponse<void>> {
    return await apiClient.post<void>(apiEndpoints.users.bulkUpdate, {
      userIds,
      updates,
    });
  }

  /**
   * Bulk deactivate users
   */
  async bulkDeactivateUsers(userIds: string[]): Promise<ApiResponse<void>> {
    return await apiClient.post<void>(apiEndpoints.users.bulkDeactivate, {
      userIds,
    });
  }

  /**
   * Get user statistics
   */
  async getUserStatistics(): Promise<ApiResponse<{
    totalUsers: number;
    activeUsers: number;
    usersByRole: Record<UserRole, number>;
    recentlyCreated: User[];
  }>> {
    return await apiClient.get<{
      totalUsers: number;
      activeUsers: number;
      usersByRole: Record<UserRole, number>;
      recentlyCreated: User[];
    }>(apiEndpoints.users.statistics);
  }

  /**
   * Get user activity/login history
   */
  async getUserActivity(userId: string): Promise<ApiResponse<Record<string, unknown>[]>> {
    return await apiClient.get<Record<string, unknown>[]>(apiEndpoints.users.activity(userId));
  }

  /**
   * Export users to CSV with current filters
   */
  async exportUsers(params: UserQueryParams = {}): Promise<Blob> {
    const queryParams = new URLSearchParams();

    // Add filters
    if (params.role) {
      params.role.forEach(role => queryParams.append('role', role.toString()));
    }
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.search) queryParams.append('search', params.search);

    const url = `${appConfig.apiBaseUrl}${apiEndpoints.users.export}?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(storageKeys.accessToken)}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('csv') || contentType.includes('text/plain') || contentType.includes('application/octet-stream')) {
          return await response.blob();
        }
      }
    } catch (e) {
      console.warn('Backend export unavailable, using client-side CSV generation:', e);
    }

    // Client-side CSV generation fallback to guarantee valid CSV (resolves BugHerd #70, #98)
    const usersResponse = await this.getUsers({ ...params, pageSize: 1000 });
    const userList = usersResponse.data?.data || [];
    
    const headers = ['Name', 'Email', 'Role', 'Company', 'Status', 'Created Date', 'Last Login', 'Department', 'Job Title', 'Phone'];
    const escapeCsvValue = (val: string) => {
      if (!val) return '';
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const rows = userList.map((u: User) => [
      escapeCsvValue(u.fullName || `${u.firstName} ${u.lastName}`.trim()),
      escapeCsvValue(u.email || ''),
      escapeCsvValue(u.role === undefined ? '' : u.role.toString()),
      escapeCsvValue(u.company?.name || ''),
      escapeCsvValue(u.isActive ? 'Active' : 'Inactive'),
      u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '',
      u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never',
      escapeCsvValue(u.department || ''),
      escapeCsvValue(u.jobTitle || ''),
      escapeCsvValue(u.phoneNumber || '')
    ].join(','));

    const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\n');
    return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  }

  /**
   * Upload user profile image
   */
  async uploadProfileImage(
    userId: string,
    file: File,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<ApiResponse<{ profileImageUrl: string }>> {
    const formData = new FormData();
    formData.append('file', file);

    return await apiClient.upload(apiEndpoints.users.profileImage(userId), file, onUploadProgress);
  }

  /**
   * Remove user profile image
   */
  async removeProfileImage(userId: string): Promise<ApiResponse<void>> {
    return await apiClient.delete<void>(apiEndpoints.users.profileImage(userId));
  }

  /**
   * Get user permissions
   */
  async getUserPermissions(userId: string): Promise<ApiResponse<string[]>> {
    return await apiClient.get<string[]>(apiEndpoints.users.permissions(userId));
  }

  /**
   * Update user permissions
   */
  async updateUserPermissions(userId: string, permissions: string[]): Promise<ApiResponse<void>> {
    return await apiClient.put<void>(apiEndpoints.users.permissions(userId), { permissions });
  }

  /**
   * Get user tickets (tickets assigned to or requested by user)
   */
  async getUserTickets(userId: string, type: 'assigned' | 'requested' = 'assigned'): Promise<ApiResponse<Ticket[]>> {
    return await apiClient.get<Ticket[]>(apiEndpoints.users.tickets(userId) + `?type=${type}`);
  }

  /**
   * Check if user exists by email
   */
  async checkUserExists(email: string): Promise<ApiResponse<{ exists: boolean; user?: Partial<User> }>> {
    return await apiClient.get<{ exists: boolean; user?: Partial<User> }>(apiEndpoints.users.checkExists + `?email=${encodeURIComponent(email)}`);
  }

  /**
   * Invite user to join
   */
  async inviteUser(email: string, role: UserRole, companyId?: string): Promise<ApiResponse<{ message: string }>> {
    return await apiClient.post<{ message: string }>(apiEndpoints.users.invite, {
      email,
      role,
      companyId,
    });
  }

  /**
   * Accept user invitation
   */
  async acceptInvitation(token: string, userData: {
    firstName: string;
    lastName: string;
    password: string;
  }): Promise<ApiResponse<User>> {
    return await apiClient.post<User>(apiEndpoints.users.acceptInvitation, {
      token,
      ...userData,
    });
  }

  /**
   * Get companies (for dropdown lists)
   */
  async getCompanies(): Promise<ApiResponse<Company[]>> {
    const response = await apiClient.get<PaginatedResponse<Company>>(apiEndpoints.companies.list);
    if (response.success) {
      // Extract just the data array from paginated response
      return {
        success: response.success,
        message: response.message,
        errors: response.errors,
        data: response.data.data || []
      };
    }
    return {
      success: false,
      message: response.message || 'Failed to fetch companies',
      errors: response.errors,
      data: []
    };
  }

  /**
   * Create company
   */
  async createCompany(companyData: {
    name: string;
    domain?: string;
  }): Promise<ApiResponse<Company>> {
    return await apiClient.post<Company>(apiEndpoints.companies.create, companyData);
  }

  /**
   * Get users in company
   */
  async getCompanyUsers(companyId: string): Promise<ApiResponse<User[]>> {
    const response = await this.getUsers({ companyId });
    if (response.success) {
      return {
        success: response.success,
        message: response.message,
        errors: response.errors,
        data: response.data.data || []
      };
    }
    return {
      success: false,
      message: response.message || 'Failed to fetch company users',
      errors: response.errors,
      data: []
    };
  }
}

// Create and export singleton instance
export const userService = new UserService();
